import BetModel from "../../src/model/BetModel";
import Layout from "../utils/Layout";
import BaseView from "./BaseView";
import Utils from "../utils/Utils";
import { TweenMax } from "gsap";
import {AbstractMoneyField, MoneyFieldConfig } from "./AbstractMoneyField";
import App from "../App";
import { IObjectConfig } from "../baseConfigs/layoutConfigShema";
import GameModel from "../model/GameModel";

type TextFontSizes = {
    money: number,
    cents: number,
    currency: number
}

export class MoneyField extends AbstractMoneyField {

    private money: number;
    protected moneyText: PIXI.Text;
    protected centsText: PIXI.Text;
    protected currencyText: PIXI.Text;
    private boundsRect: PIXI.Graphics;
    protected overrideStyle:PIXI.TextStyleOptions;
    // private _config: MoneyFieldConfig;

    constructor(config?: MoneyFieldConfig) {
        super(config);
        // if (!config) {
        //     this._config = new MoneyFieldConfig();
        // } else {
        //     this._config = config;
        // }

        this.createBoundsRect();
        this.createTextfields();
    }

    public clone():AbstractMoneyField {
        let mf = new MoneyField(Utils.deepClone(this._config));
        mf.bounds = Utils.deepClone(this.bounds);
        if(this.overrideStyle) {
            mf.style = this.overrideStyle;
        }
        return mf;
    }

    protected createBoundsRect() {
        this.boundsRect = new PIXI.Graphics();
        this.boundsRect.beginFill(0xFFFB1B, 0.5);
        this.boundsRect.drawRect(0, 0, this._config.bounds.width, this._config.bounds.height);
        this.boundsRect.endFill();
        if (!this._config.debug) {
            this.boundsRect.alpha = 0;
        }
        this.addChild(this.boundsRect);
    }

    protected createTextfields() {
        this.moneyText = new PIXI.Text("11", {
            fontFamily: this._config.fontFamily,
            fontSize: this._config.moneyFontSize
        });
        this.addChild(this.moneyText);

        this.centsText = new PIXI.Text("45", {
            fontFamily: this._config.fontFamily,
            fontSize: Math.round(this._config.moneyFontSize * this._config.centsRelativeSize / 100)
        });
        this.addChild(this.centsText);

        this.currencyText = new PIXI.Text(this._config.currency, {
            fontFamily: this._config.fontFamily,
            fontSize: Math.round(this._config.moneyFontSize * this._config.currencyRelativeSize / 100)
        });
        this.addChild(this.currencyText);
    }

    get bounds(): { width: number, height: number } {
        return {
            width: this._config.bounds.width,
            height: this._config.bounds.height
        };
    }

    set bounds(bounds: { width: number, height: number }) {
        this._config.bounds.width = bounds.width;
        this._config.bounds.height = bounds.height;
        if (this.boundsRect && this.boundsRect.parent) {
            this.boundsRect.parent.removeChild(this.boundsRect);
            this.boundsRect.destroy();
        }
        this.createBoundsRect();
        this.fitToBounds();
        this.positionText();
        // return {
        //     width: this._config.bounds.width,
        //     height: this._config.bounds.height
        // }
    }

    protected positionText() {
        const textWidth = this.textWidth;
        const moneyAscent = PIXI.TextMetrics.measureText(this.moneyText.text, this.moneyText.style).fontProperties.ascent;
        const centsAscent = PIXI.TextMetrics.measureText(this.centsText.text, this.centsText.style).fontProperties.ascent;
        const currencyAscent = PIXI.TextMetrics.measureText(this.currencyText.text, this.currencyText.style).fontProperties.ascent;

        if (this._config.bounds.height == 0) {
            this.moneyText.y = 0;
        } else {
            this.moneyText.y = (this._config.bounds.height - this.moneyText.height) / 2;
        }

        if (this._config.align == "left") {
            this.moneyText.x = 0;
        } else if (this._config.align == "right") {
            this.moneyText.x = this._config.bounds.width - textWidth;
        } else {
            this.moneyText.x = (this._config.bounds.width - textWidth) / 2;
            if (this._config.alignOnlyMoney && this.centsText.visible == false) {
                this.moneyText.x += this.currencyText.width / 2;
            }
        }

        if (this.centsText.visible) {
            this.centsText.x = this.moneyText.x + this.moneyText.width;
            this.centsText.y = this.moneyText.y + moneyAscent - centsAscent;
        }
        if (this._config.showIdentifier) {
            const visibleText = this.centsText.visible ? this.centsText : this.moneyText;
            this.currencyText.text = " " + (this._config.showSymbol ? BetModel.currency.symbol : BetModel.currency.ISO);
            this.currencyText.x = visibleText.x + visibleText.width;
            this.currencyText.y = this.moneyText.style.strokeThickness / 2 - this.currencyText.style.strokeThickness / 2 +
                this.moneyText.y + moneyAscent - currencyAscent;
        } else {
            this.currencyText.text = "";
            this.currencyText.visible = false;
        }
    }

    private getFontSizes():TextFontSizes {
        let sizes = {
            money: this._config.moneyFontSize,
            cents: Math.round(this._config.moneyFontSize * this._config.centsRelativeSize / 100),
            currency: Math.round(this._config.moneyFontSize * this._config.currencyRelativeSize / 100)
        }

        if (this._config.shouldFitToBounds)
        {
            // Calculate the ratio between the width and hight of the bounds
            const boundsRatio = this.bounds.width / this.bounds.height;
            // Calculate the ratios between the config sizes for currency and cents
            const coefCurrency: number = sizes.currency / sizes.money;
            const coefCents: number = sizes.cents / sizes.money;

            // console.log("currency: " + this.currencyText.text);
            // console.log("money: " + this.moneyText.text);
            // console.log("cents: " + this.centsText.text);

            // Get the total length of the money part of the string
            let moneyLength =
                this.moneyText.text.length -
                this.currencyText.text.length -
                0.2 - // the dot is too small so get some more space
                (this.currencyText.text.length > 0 ? 1 : 0); // if there's currency - also remove the space

            // Get the total length (with the ratios taken into account)
            const stringLength =
                this.currencyText.text.length * coefCurrency +
                moneyLength +
                this.centsText.text.length * coefCents;

            // Based on the string length and bounds - decide which dimension will be hit first
            const limit = stringLength > boundsRatio ? "width" : "height";

            let moneyFontSize: number;

            if (limit == "width") {
                // If it's the width - limit by diving the width and the length of the string
                moneyFontSize = this.bounds.width / stringLength;
            } else {
                // Otherwise it's just the height of the bounds
                moneyFontSize = this.bounds.height;
            }

            // console.log("---------");
            // console.log(
            //     "text: " +
            //         this.moneyString +
            //         this.centsString
            // );
            // console.log(
            //     "bound: " + this.bounds.width + " x " + this.bounds.height
            // );
            // console.log("length: " + stringLength);
            // console.log("limit: " + limit);
            // console.log("moneyFontSize: " + moneyFontSize);
            // console.log("---------");

            // Return the calculated sizes
            sizes = {
                money: Math.floor(moneyFontSize),
                cents: Math.floor(moneyFontSize * coefCents),
                currency: Math.floor(moneyFontSize * coefCurrency)
            }
        }

        return sizes;
    }

    private setFontSize(field: PIXI.Text, value: number) {
        if (field.style.fontSize != value) {
            field.style.fontSize = value;
            let fontConfig = GameModel.gameConfig.fonts[field.style.fontFamily as string];
            if(fontConfig && fontConfig.padding) {
                field.style.padding =  Number(field.style.fontSize)*fontConfig.padding;
                // field.style.trim = field.text != " " && field.text != null && field.text != undefined && field.text != "";
            }
        }

    }

    protected fitToBounds() {
        let sizes =  this.getFontSizes();
        this.setFontSize(this.moneyText, sizes.money);
        this.setFontSize(this.centsText, sizes.cents);
        this.setFontSize(this.currencyText, sizes.currency);
    }

    set value(value: number) {
        this.money = value;
        this.moneyText.text = this.moneyString;
        this.centsText.text = this.centsString;
        // this.moneyText.style.trim = this.moneyText.text && this.moneyText.text != " " && this.moneyText.text != "";
        // this.centsText.style.trim = this.centsText.text && this.centsText.text != " " && this.centsText.text != "";
        // this.currencyText.style.trim = this.currencyText.text && this.currencyText.text != " " && this.currencyText.text != "";
        if (this._config.hideWhenZero && value == 0) {
            this.moneyText.visible = false;
            this.centsText.visible = false;
            this.currencyText.visible = false;
        } else {
            this.moneyText.visible = true;
            this.centsText.visible = this.shouldCentsHaveValue();
        }

        this.fitToBounds();
        this.positionText();
    }

    get value() {
        return this.money;
    }

    get style() {
        return this.moneyText.style;
    }

    set style(style: PIXI.TextStyleOptions) {
        this.overrideStyle = style;
        const moneyStyle = this.moneyText.style;
        const centsStyle = this.centsText.style;
        const currencyStyle = this.currencyText.style;
        for (const key in style) {
            if (key != "fontSize" && key != "font") {
                moneyStyle[key] = style[key];
                centsStyle[key] = style[key];
                currencyStyle[key] = style[key];
            }
        }

        this.moneyText.style = moneyStyle;
        this.centsText.style = centsStyle;
        this.currencyText.style = currencyStyle;


        this.fitToBounds();
        this.positionText();
    }

    set moneyStyle(style: PIXI.TextStyleOptions) {
        this.applyStyle(this.moneyText, style);
    }

    set centsStyle(style: PIXI.TextStyleOptions) {
        this.applyStyle(this.centsText, style);
    }

    set currencyStyle(style: any) {
        this.applyStyle(this.currencyText, style);
    }

    private applyStyle(textField, style: PIXI.TextStyleOptions) {
        const textStyle = textField.style;
        for (const key in style) {
            if (key != "fontSize" && key != "font") {
                textStyle[key] = style[key];
            }
        }
        textField.style = textStyle;
        this.fitToBounds();
        this.positionText();
    }

    // set currencyText // TODO

    get textWidth() {
        // const moneyWidth = this.moneyText.context.measureText(this.moneyText.text).width;
        // const centsWidth = this.centsText.visible ? this.centsText.context.measureText(this.centsText.text).width : 0;
        // const currencyWidth = this.currencyText.context.measureText(this.currencyText.text).width;
        const moneyWidth = this.moneyText.width;
        const centsWidth = this.centsText.visible ? this.centsText.width : 0;
        const currencyWidth = this.currencyText.visible ? this.currencyText.context.measureText(this.currencyText.text).width : 0;
        return moneyWidth + centsWidth + currencyWidth;
    }

    get textHeight() {
        return Math.max(
            this.moneyText.height,
            this.centsText.height,
            !this._config.keepCurrencySize && this.currencyText.height || 0
        );
    }

    get moneyString() {
        let result;
        if (BetModel.isDisplayingCredits) {
            // if (false) {
            result = Math.floor(this.money * BetModel.denomination);
        } else {
            result = (this.money / App.config.subUnits).toFixed(
                BetModel.currency.decimals
            );
            const moneyParts = result.split(".");
            const digits = moneyParts[0].split('');

            if (this._config.spaceBetweenThousands) {
                let counter = 3;
                let digitIndex = digits.length;
                while (--digitIndex > 0) {
                    counter--;
                    if (counter == 0) {
                        counter = 3;
                        digits.splice(digitIndex, 0, this._config.symbolBetweenThousands);
                    }
                }
            }

            result = digits.join('') + (this.shouldCentsHaveValue() ? this._config.decimalSeparatorSymbol : "");
        }

        if (this._config.showIdentifier) {
            if (this._config.prependSymbol) {
                result = (this._config.showSymbol ? BetModel.currency.symbol : BetModel.currency.ISO) + " " + result;
                this.currencyText.visible = false;
            }
        }

        return result;
    }

    get centsString() {
        let result;
        if (this.shouldCentsHaveValue()) {
            result = (this.money / App.config.subUnits)
                .toFixed(BetModel.currency.decimals)
                .split(".")[1];
        }
        result = result
        ? result
        : 1;

        return result;
    }

    private shouldCentsHaveValue(): boolean {
        return !BetModel.isDisplayingCredits && BetModel.currency.decimals > 0;
        // return true;
    };

    public  rescale(bounds:IObjectConfig){
        Layout.setY(this, bounds.y);
        Layout.setX(this, bounds.x);
        Layout.testSetWidth(this, bounds.width);
    }

}
